import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Info, Save, RotateCcw, Bot } from "lucide-react";
import { getStoreId } from "../utils/auth";
import { showToast } from "../utils/toast";
import InstantSiteReconnectBanner, {
  isInstantSiteAuthError,
} from "./InstantSiteReconnectBanner";
import "./LlmsTxt.css";

const apiUrl = import.meta.env.VITE_BACKEND_URL;

const MODE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "selected", label: "Selected" },
  { value: "except", label: "All except selected" },
];

const FREQUENCY_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DEFAULT_LICENSE_LABELS = {
  full_commercial: "Allow full commercial training",
  display_noncommercial: "Allow display & non-commercial training",
  display_no_training: "Allow display, forbid training",
  forbid_all: "Forbid all AI use",
};

function statusBadge(status) {
  if (status === "created") return { label: "LLMS FILE CREATED", className: "badge-success" };
  if (status === "failed") return { label: "LLMS FILE FAILED", className: "badge-danger" };
  return { label: "LLMS FILE NOT CREATED", className: "badge-secondary" };
}

function defaultForm() {
  return {
    includeCategories: true,
    includeProducts: true,
    includePages: false,
    categoriesMode: "all",
    productsMode: "all",
    pagesMode: "all",
    categoryIds: [],
    productIds: [],
    pageIds: [],
    description: "",
    updateFrequency: "manual",
    licenseType: "display_noncommercial",
    status: "not_created",
    lastError: null,
    cdnLlmsUrl: null,
    cdnLlmsFullUrl: null,
    redirectWarning: null,
  };
}

export default function LlmsTxt({ onOpenPricing }) {
  const hashSiteId = getStoreId();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState([]);
  const [publishAllowed, setPublishAllowed] = useState(false);
  const [licenseLabels, setLicenseLabels] = useState(DEFAULT_LICENSE_LABELS);
  const [howItWorksOpen, setHowItWorksOpen] = useState(true);
  const [storeSecureUrl, setStoreSecureUrl] = useState("");
  const [instantSiteHint, setInstantSiteHint] = useState(null);

  const storefrontOrigin = (() => {
    if (!storeSecureUrl) return "";
    try {
      const u = new URL(/^https?:\/\//i.test(storeSecureUrl) ? storeSecureUrl : `https://${storeSecureUrl}`);
      return `${u.protocol}//${u.host}`;
    } catch {
      return storeSecureUrl.replace(/\/+$/, "");
    }
  })();

  const storefrontLlmsUrl = storefrontOrigin ? `${storefrontOrigin}/llms.txt` : "";
  const storefrontLlmsFullUrl = storefrontOrigin ? `${storefrontOrigin}/llms-full.txt` : "";

  const api = useCallback(
    (path, opts) => {
      const base = `${apiUrl}/api/llms-txt${path}`;
      const sep = base.includes("?") ? "&" : "?";
      return fetch(`${base}${sep}id=${encodeURIComponent(hashSiteId)}`, opts);
    },
    [hashSiteId],
  );

  const loadSettings = useCallback(async () => {
    if (!hashSiteId) return;
    setLoading(true);
    try {
      const res = await api("/settings");
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load settings");
      setSaved(data.settings);
      setForm(data.settings);
      setPublishAllowed(Boolean(data.publishAllowed));
      if (data.licenseLabels) setLicenseLabels(data.licenseLabels);
      if (typeof data.storeSecureUrl === "string") {
        setStoreSecureUrl(String(data.storeSecureUrl).replace(/\/+$/, ""));
      }
      const warn = data.settings?.redirectWarning || data.redirectWarning || null;
      if (isInstantSiteAuthError(warn)) {
        setInstantSiteHint(warn);
      }
    } catch (err) {
      showToast(err.message || "Failed to load settings", "error");
      const fallback = defaultForm();
      setSaved(fallback);
      setForm(fallback);
    } finally {
      setLoading(false);
    }
  }, [api, hashSiteId]);

  const loadEntities = useCallback(async () => {
    if (!hashSiteId) return;
    try {
      const [catRes, prodRes, pageRes] = await Promise.all([
        api("/entities?type=categories"),
        api("/entities?type=products"),
        api("/entities?type=pages"),
      ]);
      const [catData, prodData, pageData] = await Promise.all([
        catRes.json(),
        prodRes.json(),
        pageRes.json(),
      ]);
      if (catRes.ok) setCategories(catData.items || []);
      if (prodRes.ok) setProducts(prodData.items || []);
      if (pageRes.ok) setPages(pageData.items || []);
    } catch {
      // pickers stay empty
    }
  }, [api, hashSiteId]);

  useEffect(() => {
    loadSettings();
    loadEntities();
  }, [loadSettings, loadEntities]);

  const dirty = useMemo(() => {
    if (!saved || !form) return false;
    return JSON.stringify(saved) !== JSON.stringify(form);
  }, [saved, form]);

  const patch = (key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const settingsPayload = (source) => ({
    includeCategories: source.includeCategories,
    includeProducts: source.includeProducts,
    includePages: source.includePages,
    categoriesMode: source.categoriesMode,
    productsMode: source.productsMode,
    pagesMode: source.pagesMode,
    categoryIds: source.categoryIds,
    productIds: source.productIds,
    pageIds: source.pageIds,
    description: source.description,
    updateFrequency: source.updateFrequency,
    licenseType: source.licenseType,
  });

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const res = await api("/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settingsPayload(form)),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save");
      setSaved(data.settings);
      setForm(data.settings);
      showToast("Settings saved successfully", "success");
    } catch (err) {
      showToast(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (saved) setForm(saved);
  };

  const handleCreate = async () => {
    if (!form) return;
    if (!publishAllowed) {
      showToast("Upgrade to a paid plan to create LLMs.txt files.", "error");
      onOpenPricing?.();
      return;
    }
    setCreating(true);
    try {
      const res = await api("/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settingsPayload(form)),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.upgradeRequired) onOpenPricing?.();
        throw new Error(data.error || "Failed to create LLMs file");
      }
      setSaved(data.settings);
      setForm(data.settings);
      if (data.storeSecureUrl) {
        setStoreSecureUrl(String(data.storeSecureUrl).replace(/\/+$/, ""));
      }
      if (data.redirectWarning) {
        showToast(data.redirectWarning, "warning");
        if (isInstantSiteAuthError(data.redirectWarning)) {
          setInstantSiteHint(data.redirectWarning);
        }
      } else {
        showToast("LLMs files created and redirects updated", "success");
      }
    } catch (err) {
      const msg = err.message || "Failed to create LLMs file";
      showToast(msg, "error");
      if (isInstantSiteAuthError(msg)) {
        setInstantSiteHint(msg);
      }
      await loadSettings();
    } finally {
      setCreating(false);
    }
  };

  const renderEntityPicker = (items, selected, onChange, mode) => {
    if (mode === "all") return null;
    return (
      <select
        className="form-select form-select-sm mt-2"
        multiple
        size={Math.min(8, Math.max(4, items.length || 4))}
        value={(selected || []).map(String)}
        onChange={(e) => {
          const ids = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
          onChange(ids.filter((n) => n > 0));
        }}
      >
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </select>
    );
  };

  if (!hashSiteId) {
    return (
      <div className="llms-txt-settings">
        <div className="card-panel text-muted text-center py-5">Store context not found. Please reload the app.</div>
      </div>
    );
  }

  if (loading || !form) {
    return (
      <div className="llms-txt-settings">
        <div className="card-panel text-muted text-center py-5">Loading LLMs.txt settings…</div>
      </div>
    );
  }

  const badge = statusBadge(form.status);
  const licenseOptions = Object.keys(licenseLabels).map((value) => ({
    value,
    label: licenseLabels[value],
  }));

  return (
    <div className="llms-txt-settings">
      <div className="llms-header d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0 fw-bold">
            <Bot size={22} className="me-2" style={{ verticalAlign: "-4px" }} />
            LLMs.txt: AI Traffic Booster
          </h4>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setHowItWorksOpen(!howItWorksOpen)}
          >
            How it works
            <Info size={14} className="ms-1" />
          </button>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {dirty && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleDiscard}
                disabled={saving || creating}
              >
                <RotateCcw size={14} className="me-1" />
                Discard
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleSave}
                disabled={saving || creating}
              >
                <Save size={14} className="me-1" />
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
          <button
            type="button"
            className="btn btn-dark"
            onClick={handleCreate}
            disabled={creating || saving || !publishAllowed}
            title={publishAllowed ? undefined : "Available on paid plans"}
          >
            {creating ? "Creating…" : "Create LLMs file"}
          </button>
        </div>
      </div>

      {howItWorksOpen && (
        <div className="alert alert-light mb-3 how-it-works">
          <div className="d-flex align-items-start">
            <Info className="me-2 text-secondary flex-shrink-0 mt-1" size={18} />
            <div className="small text-muted">
              <strong className="text-dark">How it works:</strong>{" "}
              llms.txt helps AI assistants like ChatGPT, Claude, Gemini, and Perplexity understand
              your store. It provides a structured summary of your products, categories, policies,
              and key content. This helps AI deliver more accurate product recommendations and
              answers about your business. Keeping llms.txt up to date improves your store&apos;s
              visibility in AI-powered search and shopping experiences.
            </div>
          </div>
        </div>
      )}

      {!publishAllowed && (
        <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="small mb-0">
            <strong>Trial plan:</strong> You can configure LLMs.txt settings, but creating and
            publishing files requires a paid plan.
          </div>
          <button type="button" className="btn btn-sm btn-warning" onClick={() => onOpenPricing?.()}>
            Upgrade
          </button>
        </div>
      )}

      <InstantSiteReconnectBanner
        forceShow={isInstantSiteAuthError(form.redirectWarning) || isInstantSiteAuthError(instantSiteHint)}
        hintError={instantSiteHint || form.redirectWarning}
      />

      {form.redirectWarning && !isInstantSiteAuthError(form.redirectWarning) && (
        <div className="alert alert-warning small mb-3">{form.redirectWarning}</div>
      )}

      <div className="row g-3">
        <div className="col-lg-8">
          <div className="card-panel mb-3">
            <h2 className="h6 fw-semibold">Content to include</h2>
            <p className="small text-muted">Select the content types you want to include in LLMs.txt</p>

            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="llms-collections"
                checked={!!form.includeCategories}
                onChange={(e) => patch("includeCategories", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="llms-collections">
                Categories
              </label>
            </div>
            {form.includeCategories && (
              <>
                <select
                  className="form-select form-select-sm"
                  value={form.categoriesMode}
                  onChange={(e) => patch("categoriesMode", e.target.value)}
                >
                  {MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {renderEntityPicker(categories, form.categoryIds, (ids) => patch("categoryIds", ids), form.categoriesMode)}
              </>
            )}

            <div className="form-check mb-2 mt-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="llms-products"
                checked={!!form.includeProducts}
                onChange={(e) => patch("includeProducts", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="llms-products">
                Products
              </label>
            </div>
            {form.includeProducts && (
              <>
                <select
                  className="form-select form-select-sm"
                  value={form.productsMode}
                  onChange={(e) => patch("productsMode", e.target.value)}
                >
                  {MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {renderEntityPicker(products, form.productIds, (ids) => patch("productIds", ids), form.productsMode)}
              </>
            )}

            <div className="form-check mb-2 mt-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="llms-pages"
                checked={!!form.includePages}
                onChange={(e) => patch("includePages", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="llms-pages">
                Pages (Instant Site, when available)
              </label>
            </div>
            {form.includePages && (
              <>
                <select
                  className="form-select form-select-sm"
                  value={form.pagesMode}
                  onChange={(e) => patch("pagesMode", e.target.value)}
                >
                  {MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {renderEntityPicker(pages, form.pageIds, (ids) => patch("pageIds", ids), form.pagesMode)}
              </>
            )}

            <label className="form-label small mt-3" htmlFor="llms-description">
              Store description (optional)
            </label>
            <textarea
              className="form-control form-control-sm"
              id="llms-description"
              rows={3}
              value={form.description || ""}
              onChange={(e) => patch("description", e.target.value)}
            />
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card-panel mb-3">
            <div className="small text-muted mb-2">Files status:</div>
            <span className={`badge ${badge.className}`}>{badge.label}</span>
            {form.lastError && <div className="small text-danger mt-2">{form.lastError}</div>}
            {(form.status === "created" || form.cdnLlmsUrl) && storefrontLlmsUrl && (
              <div className="small mt-2">
                <a href={storefrontLlmsUrl} target="_blank" rel="noreferrer">
                  llms.txt
                </a>
                {storefrontLlmsFullUrl && (
                  <>
                    {" · "}
                    <a href={storefrontLlmsFullUrl} target="_blank" rel="noreferrer">
                      llms-full.txt
                    </a>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="card-panel mb-3">
            <h2 className="h6 fw-semibold">Update frequency</h2>
            <p className="small text-muted">
              We will automatically recreate the llms.txt file to keep it up to date.
            </p>
            <select
              className="form-select form-select-sm"
              value={form.updateFrequency}
              onChange={(e) => patch("updateFrequency", e.target.value)}
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="card-panel">
            <h2 className="h6 fw-semibold">License type</h2>
            <p className="small text-muted">Define how LLMs can use the content of your store.</p>
            <select
              className="form-select form-select-sm"
              value={form.licenseType}
              onChange={(e) => patch("licenseType", e.target.value)}
            >
              {licenseOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
